import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Toolbar from './components/Toolbar';
import Artboard from './components/Artboard';
import PropertiesPanel from './components/PropertiesPanel';
import Header from './components/Header';
import useEditorStore from './store/useEditorStore';
import Projects from './pages/Projects';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PublicArtboardView from './components/PublicArtboardView';
import ProtectedRoute from './components/ProtectedRoute';
import { loadFonts, preloadCriticalFonts } from './utils/fontLoader';
import { FaBars, FaTimes, FaCog } from 'react-icons/fa';

const EditorLayout: React.FC = () => {
  const {
    artboardWidth,
    artboardHeight,
    artboardBackgroundColor,
    artboardBackgroundImage,
  } = useEditorStore();
  const [editorReady, setEditorReady] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('Initializing...');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingText, setLoadingText] = useState('Initializing...');
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);
  const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);

  useEffect(() => {
    const initializeFonts = async () => {
      try {
        setLoadingText('Loading fonts...');
        setLoadingStatus('Loading fonts...');
        await preloadCriticalFonts();
        setLoadingText('Initializing editor...');
        setLoadingStatus('Initializing editor...');
        await loadFonts();
      } catch (error) {
        // Font loading failed, proceed anyway
      }
      setIsLoading(false);
    };
    initializeFonts();
  }, []);

  useEffect(() => {
    let fontsReady = false;
    let canvasReady = false;
    let objectsLoaded = false;
    let allReady = false;

    const checkIfReady = () => {
      if (fontsReady && canvasReady && objectsLoaded && !allReady) {
        allReady = true;
        setTimeout(() => {
          setEditorReady(true);
        }, 200);
      }
    };

    const handleFontsReady = () => {
      fontsReady = true;
      setLoadingStatus('Canvas initializing...');
      checkIfReady();
    };
    const handleCanvasReady = () => {
      canvasReady = true;
      setLoadingStatus('Loading objects...');
      checkIfReady();
    };
    const handleObjectsLoaded = () => {
      objectsLoaded = true;
      setLoadingStatus('Finalizing...');
      setTimeout(() => {
        checkIfReady();
      }, 300);
    };

    window.addEventListener('fontsReady', handleFontsReady);
    window.addEventListener('canvasReady', handleCanvasReady);
    window.addEventListener('objectsLoaded', handleObjectsLoaded);
    const fallbackTimer = setTimeout(() => {
      if (!allReady) {
        setEditorReady(true);
      }
    }, 5000);
    return () => {
      window.removeEventListener('fontsReady', handleFontsReady);
      window.removeEventListener('canvasReady', handleCanvasReady);
      window.removeEventListener('objectsLoaded', handleObjectsLoaded);
      clearTimeout(fallbackTimer);
    };
  }, []);

  const showOverlay = isLoading || !editorReady;

  const closeSidebars = () => {
    setIsToolbarOpen(false);
    setIsPropertiesOpen(false);
  };

  return (
    <>
      {showOverlay && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="flex flex-col items-center justify-center">
            <div className="relative h-10 w-10">
              <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-white" />
              <div className="absolute inset-1 animate-spin rounded-full border-2 border-transparent border-b-white/50" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
            </div>
            <div className="mt-4 text-sm font-medium text-white/80">{isLoading ? loadingText : loadingStatus}</div>
          </div>
        </div>
      )}
      <div className="relative flex h-[calc(100vh-48px)] bg-gray-100 text-gray-900 dark:bg-gray-950 dark:text-white">
        {/* Mobile overlay backdrop */}
        <div
          className={`fixed inset-0 z-[998] bg-black/50 transition-all duration-300 md:hidden ${
            isToolbarOpen || isPropertiesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          onClick={closeSidebars}
        />
        
        {/* Toolbar - Fixed on mobile, static on desktop */}
        <div
          className={`fixed left-0 top-0 bottom-0 z-[999] transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${
            isToolbarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <Toolbar />
        </div>
        
        {/* Main artboard area */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="relative flex-1 overflow-hidden" style={{ backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.07) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
            <Artboard
              width={artboardWidth}
              height={artboardHeight}
              backgroundColor={artboardBackgroundColor}
              backgroundImage={artboardBackgroundImage || undefined}
            />
          </div>
        </div>
        
        {/* Properties Panel - Fixed on mobile, static on desktop */}
        <div
          className={`fixed right-0 top-0 bottom-0 z-[999] flex w-[85%] max-w-[320px] flex-col border-l border-gray-200/60 bg-white shadow-[-4px_0_16px_rgba(0,0,0,0.2)] transition-transform duration-300 ease-in-out dark:border-gray-700/60 dark:bg-gray-900 md:relative md:w-[300px] md:translate-x-0 md:shadow-none ${
            isPropertiesOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'
          }`}
        >
          <PropertiesPanel />
        </div>
        
        {/* Mobile toggle buttons - Visible on mobile, hidden on desktop */}
        <button
          onClick={() => {
            setIsToolbarOpen(!isToolbarOpen);
            setIsPropertiesOpen(false);
          }}
          title="Toggle Tools"
          className="fixed bottom-5 left-3 z-[1000] flex h-10 w-10 items-center justify-center rounded-full border-none bg-blue-500 text-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] transition-all hover:scale-110 hover:bg-blue-600 active:scale-95 md:hidden"
        >
          {isToolbarOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </button>
        <button
          onClick={() => {
            setIsPropertiesOpen(!isPropertiesOpen);
            setIsToolbarOpen(false);
          }}
          title="Toggle Properties"
          className="fixed bottom-5 right-3 z-[1000] flex h-10 w-10 items-center justify-center rounded-full border-none bg-blue-500 text-white shadow-[0_4px_12px_rgba(0,0,0,0.3)] transition-all hover:scale-110 hover:bg-blue-600 active:scale-95 md:hidden"
        >
          {isPropertiesOpen ? <FaTimes size={20} /> : <FaCog size={20} />}
        </button>
      </div>
    </>
  );
};

const ProtectedRouteWithHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <>
      <Header />
      <ProtectedRoute>
        {children}
      </ProtectedRoute>
    </>
  );
};

const AuthRedirect: React.FC = () => {
  const token = localStorage.getItem('token');
  
  if (token) {
    return <Navigate to="/projects" replace />;
  }

  return <Login />;
};

const App: React.FC = () => {
  const { isDarkMode } = useEditorStore();

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<AuthRedirect />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/public/:id" element={<PublicArtboardView />} />
        
        <Route path="/projects" element={
          <ProtectedRouteWithHeader>
            <Projects />
          </ProtectedRouteWithHeader>
        } />
        <Route path="/settings" element={
          <ProtectedRouteWithHeader>
            <Settings />
          </ProtectedRouteWithHeader>
        } />
        <Route path="/editor" element={
          <ProtectedRouteWithHeader>
            <EditorLayout />
          </ProtectedRouteWithHeader>
        } />
        <Route path="/editor/:id" element={
          <ProtectedRouteWithHeader>
            <EditorLayout />
          </ProtectedRouteWithHeader>
        } />
      </Routes>
    </Router>
  );
};

export default App;